import { useState, useEffect } from "react";
import { enqueueSnackbar } from "notistack";
import { Play, Pause, Square, UserPlus, Clock, Image as ImageIcon, Users, FileText } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import Input from "../../components/Input";
import { SelectSingle } from "../../components/SelectSingle";
import { FormButton } from "../../components/FormButton";
import { relatorioMovimentoService, RelatorioMovimentoResponsavel } from "../../services/relatorioMovimento";
import { useAuth } from "../../contexts/AuthContext";
import { usePermissions } from "../../hooks/usePermissions";
import moment from "moment";
import { api } from "../../services/api";
import { SkeletonResponsavel, FadeInWrapper } from "../../components/Skeleton";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";

export function RelatorioMovimento() {
  const { militar } = useAuth();
  const { hasPermission } = usePermissions();

  const [responsavelAtual, setResponsavelAtual] = useState<RelatorioMovimentoResponsavel | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingResponsavel, setLoadingResponsavel] = useState(true);
  const [tempoResponsavel, setTempoResponsavel] = useState("");

  // Formulário de relatório
  const [horario, setHorario] = useState("");
  const [pessoasQG, setPessoasQG] = useState("");
  const [printUrl, setPrintUrl] = useState("");
  const [oficiaisOnline, setOficiaisOnline] = useState<string[]>([]);
  const [oficiaisDisponiveis, setOficiaisDisponiveis] = useState<any[]>([]);
  const [oficiaisSelecionados, setOficiaisSelecionados] = useState<any[]>([]);

  const canManage = hasPermission("central.rm.gerenciar");

  // Modais de confirmação
  const [modalPausarRMAberta, setModalPausarRMAberta] = useState(false);
  const [modalFinalizarRMAberta, setModalFinalizarRMAberta] = useState(false);

  const horarios = Array.from({ length: 14 }, (_, i) => {
    const hour = 10 + i;
    return {
      value: `${hour}:00`,
      label: `${hour}:00`,
    };
  });

  useEffect(() => {
    loadResponsavelAtual();
    loadOficiais();

    // Atualização automática a cada 20 segundos
    const autoUpdateInterval = setInterval(() => {
      loadResponsavelAtual();
    }, 20000);

    return () => clearInterval(autoUpdateInterval);
  }, []);

  // Contador de tempo para responsável
  useEffect(() => {
    if (!responsavelAtual?.data_hora_inicio || responsavelAtual.status === "pausado") {
      setTempoResponsavel("");
      return;
    }

    const updateTimer = () => {
      const inicio = moment(responsavelAtual.data_hora_inicio);
      const agora = moment();
      const tempoTotalMs = agora.diff(inicio);

      // Subtrair tempo pausado (em milissegundos)
      const tempoPausadoMs = (responsavelAtual.tempo_pausado_segundos || 0) * 1000;
      const tempoAtivoMs = tempoTotalMs - tempoPausadoMs;

      const duration = moment.duration(tempoAtivoMs);

      const horas = Math.floor(duration.asHours());
      const minutos = duration.minutes();
      const segundos = duration.seconds();

      setTempoResponsavel(`${horas.toString().padStart(2, '0')}:${minutos.toString().padStart(2, '0')}:${segundos.toString().padStart(2, '0')}`);
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [responsavelAtual]);

  const loadResponsavelAtual = async () => {
    try {
      setLoadingResponsavel(true);
      const data = await relatorioMovimentoService.getResponsavelAtual();
      setResponsavelAtual(data);
    } catch (error) {
      console.error("Erro ao carregar responsável:", error);
    } finally {
      setLoadingResponsavel(false);
    }
  };

  const loadOficiais = async () => {
    try {
      const response = await api.get("/militares/listOficiais");
      setOficiaisDisponiveis(response.data);
    } catch (error) {
      console.error("Erro ao carregar oficiais:", error);
    }
  };

  const handleIniciarRM = async () => {
    try {
      setLoading(true);
      const data = await relatorioMovimentoService.iniciarRM();
      setResponsavelAtual(data);
      enqueueSnackbar("RM iniciado com sucesso!", { variant: "success" });
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao iniciar RM", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePegarRM = async () => {
    try {
      setLoading(true);
      const data = await relatorioMovimentoService.pegarRM();
      setResponsavelAtual(data);
      enqueueSnackbar("Você assumiu o RM!", { variant: "success" });
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao pegar RM", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePausarRM = async () => {
    try {
      setLoading(true);
      const data = await relatorioMovimentoService.pausarRM();
      setResponsavelAtual(data); // Mantém responsável com status pausado
      enqueueSnackbar("RM pausado!", { variant: "success" });
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao pausar RM", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRetomarRM = async () => {
    try {
      setLoading(true);
      const data = await relatorioMovimentoService.retomarRM();
      setResponsavelAtual(data); // Atualiza para status ativo
      enqueueSnackbar("RM retomado!", { variant: "success" });
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao retomar RM", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFinalizarRM = async () => {
    try {
      setLoading(true);
      await relatorioMovimentoService.finalizarRM();
      setResponsavelAtual(null);
      enqueueSnackbar("RM finalizado!", { variant: "success" });
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao finalizar RM", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleMoverParaSelecionados = (oficial: any) => {
    setOficiaisDisponiveis((prev) => prev.filter((o) => o.id !== oficial.id));
    setOficiaisSelecionados((prev) => [...prev, oficial]);
    setOficiaisOnline((prev) => [...prev, oficial.id]);
  };

  const handleMoverParaDisponiveis = (oficial: any) => {
    setOficiaisSelecionados((prev) => prev.filter((o) => o.id !== oficial.id));
    setOficiaisDisponiveis((prev) => [...prev, oficial]);
    setOficiaisOnline((prev) => prev.filter((id) => id !== oficial.id));
  };

  const handleSubmitRelatorio = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!horario || !pessoasQG || oficiaisSelecionados.length === 0) {
      enqueueSnackbar("Preencha todos os campos obrigatórios", {
        variant: "error",
      });
      return;
    }

    try {
      setLoading(true);
      const payload = {
        horario,
        pessoas_qg: pessoasQG,
        print_url: printUrl || undefined,
        oficiais_online: oficiaisSelecionados.map(o => o.id),
      };

      await relatorioMovimentoService.create(payload);

      enqueueSnackbar("Relatório criado com sucesso!", { variant: "success" });

      // Limpar formulário
      setHorario("");
      setPessoasQG("");
      setPrintUrl("");
      setOficiaisSelecionados([]);
      loadOficiais();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao criar relatório", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const souEu = responsavelAtual && responsavelAtual.id_militar === militar?.id;

  return (
    <div className="space-y-6">
      {/* Sistema de Responsabilidade */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Sistema de Responsabilidade - RM
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loadingResponsavel ? (
            <SkeletonResponsavel />
          ) : (
            <div className="space-y-4">
              {responsavelAtual ? (
                <FadeInWrapper>
                  <div className="flex items-start gap-4 p-4 bg-gray-100 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                    <div className="flex-shrink-0">
                      <img
                        src={`https://www.habbo.com.br/habbo-imaging/avatarimage?&user=${responsavelAtual.militar?.nick}&action=std&direction=2&head_direction=2&img_format=png&gesture=std&headonly=0&size=l`}
                        alt={responsavelAtual.militar?.nick}
                        className="w-24 h-24 object-cover"
                        style={{ imageRendering: 'pixelated' }}
                      />
                    </div>
                    <div className="flex-1">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Responsável Atual</p>
                          <p className="font-semibold text-gray-900 dark:text-gray-100">
                            {responsavelAtual.militar?.patente?.abreviacao} {responsavelAtual.militar?.nick}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            Desde: {moment(responsavelAtual.data_hora_inicio).format("DD/MM/YYYY HH:mm")}
                          </p>
                          {tempoResponsavel && (
                            <div className="flex items-center gap-1 mt-1 text-xs font-mono text-blue-600 dark:text-blue-400">
                              <Clock className="h-3 w-3" />
                              {tempoResponsavel}
                            </div>
                          )}
                        </div>
                        <Badge variant={responsavelAtual.status === "ativo" || responsavelAtual.status === "retomou" ? "default" : "secondary"}>
                          {responsavelAtual.status === "ativo" ? "Ativo" : responsavelAtual.status === "retomou" ? "Retomou" : "Pausado"}
                        </Badge>
                      </div>
                    </div>
                  </div>

                  {canManage && (
                    <div className="flex gap-2 flex-wrap">
                      {!souEu && (
                        <Button onClick={handlePegarRM} disabled={loading}>
                          <UserPlus className="h-4 w-4 mr-2" />
                          Pegar RM
                        </Button>
                      )}
                      {souEu && (
                        <>
                          {(responsavelAtual.status === "ativo" || responsavelAtual.status === "retomou") && (
                            <Button onClick={() => setModalPausarRMAberta(true)} disabled={loading} variant="outline">
                              <Pause className="h-4 w-4 mr-2" />
                              Pausar RM
                            </Button>
                          )}
                          {responsavelAtual.status === "pausado" && (
                            <Button onClick={handleRetomarRM} disabled={loading} variant="outline">
                              <Play className="h-4 w-4 mr-2" />
                              Retomar RM
                            </Button>
                          )}
                          <Button onClick={() => setModalFinalizarRMAberta(true)} disabled={loading} variant="destructive">
                            <Square className="h-4 w-4 mr-2" />
                            Finalizar RM
                          </Button>
                        </>
                      )}
                    </div>
                  )}
                </FadeInWrapper>
              ) : (
                <>
                  <p className="text-muted-foreground">Nenhum responsável ativo no momento</p>
                  {canManage && (
                    <Button onClick={handleIniciarRM} disabled={loading}>
                      <Play className="h-4 w-4 mr-2" />
                      Iniciar RM
                    </Button>
                  )}
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Formulário de Relatório */}
      {canManage && responsavelAtual && souEu && (responsavelAtual.status === "ativo" || responsavelAtual.status === "retomou") && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Registrar Relatório de Movimento
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmitRelatorio} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <SelectSingle
                    title="Horário"
                    required
                    value={horario}
                    onChange={(e) => setHorario(e.target.value)}
                  >
                    <option value="">Selecione o horário</option>
                    {horarios.map((h) => (
                      <option key={h.value} value={h.value}>
                        {h.label}
                      </option>
                    ))}
                  </SelectSingle>
                </div>

                <div>
                  <Input
                    title="Pessoas no QG"
                    required
                    type="number"
                    value={pessoasQG}
                    onChange={(e) => setPessoasQG(e.target.value)}
                    placeholder="Quantidade de pessoas"
                    min="0"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Link do Print *</label>
                <input
                  type="url"
                  value={printUrl}
                  onChange={(e) => setPrintUrl(e.target.value)}
                  placeholder="https://..."
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Oficiais Online *</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="border rounded-lg p-4">
                    <p className="text-sm font-medium mb-2">Disponíveis</p>
                    <div className="space-y-1 max-h-64 overflow-y-auto">
                      {oficiaisDisponiveis.map((oficial) => (
                        <div
                          key={oficial.id}
                          className="flex items-center justify-between p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded cursor-pointer transition-colors"
                          onClick={() => handleMoverParaSelecionados(oficial)}
                        >
                          <div className="flex items-center gap-2">
                            <img
                              src={`https://www.habbo.com.br/habbo-imaging/avatarimage?&user=${oficial.nick}&action=std&direction=2&head_direction=2&img_format=png&gesture=std&headonly=1&size=l`}
                              alt={oficial.nick}
                              className="w-10 h-10 object-contain rounded-full"
                              style={{ imageRendering: 'pixelated' }}
                            />
                            <span className="text-sm text-gray-900 dark:text-gray-100">
                              {oficial.patente?.abreviacao} {oficial.nick}
                            </span>
                          </div>
                          <span className="text-xs text-gray-500 dark:text-gray-400">→</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="border rounded-lg p-4">
                    <p className="text-sm font-medium mb-2">Selecionados ({oficiaisSelecionados.length})</p>
                    <div className="space-y-1 max-h-64 overflow-y-auto">
                      {oficiaisSelecionados.map((oficial) => (
                        <div
                          key={oficial.id}
                          className="flex items-center justify-between p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded cursor-pointer transition-colors"
                          onClick={() => handleMoverParaDisponiveis(oficial)}
                        >
                          <span className="text-xs text-gray-500 dark:text-gray-400">←</span>
                          <div className="flex items-center gap-2">
                            <img
                              src={`https://www.habbo.com.br/habbo-imaging/avatarimage?&user=${oficial.nick}&action=std&direction=2&head_direction=2&img_format=png&gesture=std&headonly=1&size=l`}
                              alt={oficial.nick}
                              className="w-10 h-10 object-contain rounded-full"
                              style={{ imageRendering: 'pixelated' }}
                            />
                            <span className="text-sm text-gray-900 dark:text-gray-100">
                              {oficial.patente?.abreviacao} {oficial.nick}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <FormButton loading={loading} type="submit">
                Registrar Relatório
              </FormButton>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Modal Confirmar Pausar RM */}
      <Dialog open={modalPausarRMAberta} onOpenChange={setModalPausarRMAberta}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Pausar RM</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja pausar o Relatório de Movimento?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalPausarRMAberta(false)}>
              Cancelar
            </Button>
            <Button onClick={() => { setModalPausarRMAberta(false); handlePausarRM(); }} disabled={loading}>
              Confirmar Pausa
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Confirmar Finalizar RM */}
      <Dialog open={modalFinalizarRMAberta} onOpenChange={setModalFinalizarRMAberta}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Finalizar RM</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja finalizar o Relatório de Movimento? Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalFinalizarRMAberta(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={() => { setModalFinalizarRMAberta(false); handleFinalizarRM(); }} disabled={loading}>
              Finalizar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
